import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { Button, Checkbox, Notification, PageTopBar, PageView } from '../../../components'
import { Loader } from '../../../components/Loader'
import { UserContext } from '../../../hooks/UserContext'
import { fetchStores } from '../../../services/storeService'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { fetchStoreAllowedShippingPackages, updateStoreAllowedShippingPackages } from '../../../services/storeAllowedShippingPackagesServices'
import { isSuperAdminUser } from '../../../utils/userUtils'

const T = 'shipping_packages.store_allowed'
const MIN_QUERY_LENGTH = 2
const inputClasses = 'shadow-sm block w-full sm:text-sm rounded-md border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500'

const sameIds = (draft, savedIds) => draft.size === savedIds.length && savedIds.every(id => draft.has(id))
const statusOf = error => error?.response?.status

// Configuraciones → Cajas por marca: which shipping boxes one brand may use in this warehouse.
export const StoreAllowedShippingPackagesContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()
  const canUpdate = !!isSuperAdminUser(user) || !!user?.permissions?.update_shipping_packages

  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const notify = (type, title) => setNotification({ show: true, type, title })

  const [store, setStore] = useState(null)
  const [draft, setDraft] = useState(new Set())

  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    enabled: !!warehouse_id,
    refetchOnWindowFocus: false,
  })

  // The brand search is scoped to this warehouse, so a different warehouse starts over.
  useEffect(() => setStore(null), [warehouse_id])

  const queryKey = ['store_allowed_shipping_packages', store?.id, warehouse_id]
  const query = useQuery(queryKey, () => fetchStoreAllowedShippingPackages(store.id, warehouse_id), {
    enabled: !!store,
    refetchOnWindowFocus: false,
    // Each successful fetch resets the draft, so a silent reconnect refetch would wipe unsaved ticks.
    refetchOnReconnect: false,
    retry: (failures, error) => statusOf(error) !== 404 && failures < 1,
  })
  const { data, dataUpdatedAt } = query

  // Every successful fetch (including the refetch after a save or a rejected save) resets the ticks
  // to what the server holds.
  useEffect(() => {
    if (data) setDraft(new Set(data.allowed_ids))
  }, [data, dataUpdatedAt])

  const mutation = useMutation(ids => updateStoreAllowedShippingPackages(store.id, warehouse_id, ids), {
    onSuccess: () => {
      notify('success', t(`${T}.save_success`))
      queryClient.invalidateQueries(queryKey)
    },
    onError: error => {
      const stale = error?.response?.data?.code === 'PACKAGE_NOT_A_CANDIDATE'
      notify('error', t(stale ? `${T}.not_a_candidate` : `${T}.save_error`))
      if (stale) queryClient.invalidateQueries(queryKey)
    },
  })

  const toggle = (id, checked) =>
    setDraft(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })

  const dirty = !!data && !sameIds(draft, data.allowed_ids)
  const save = () => mutation.mutate([...draft].sort((a, b) => a - b))

  const renderChecklist = () => {
    if (query.isError && statusOf(query.error) === 404) {
      return <p className="text-sm text-gray-600 py-6">{t(`${T}.not_in_warehouse`)}</p>
    }
    if (query.isError && !data) {
      return (
        <div className="py-6">
          <p className="text-red-600 font-medium mb-3">{t(`${T}.load_error`)}</p>
          <Button type="secondary" onClick={() => query.refetch()}>
            {t(`${T}.retry`)}
          </Button>
        </div>
      )
    }
    if (!data) return <Loader show={true} />

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{t(`${T}.explanation`, { store: store.name })}</p>
        {data.candidates.length === 0 ? (
          <p className="text-sm text-gray-500">{t(`${T}.empty`)}</p>
        ) : (
          <ul className="border border-gray-200 rounded-md divide-y divide-gray-100 bg-white">
            {data.candidates.map(candidate => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                storeId={store.id}
                checked={draft.has(candidate.id)}
                disabled={!canUpdate || mutation.isLoading}
                onChange={checked => toggle(candidate.id, checked)}
              />
            ))}
          </ul>
        )}
        <Button type="primary" onClick={save} disabled={!canUpdate || !dirty} loading={mutation.isLoading}>
          {t(`${T}.save`)}
        </Button>
      </div>
    )
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">
            {t(`${T}.title`)}
            {warehouse?.name && <span className="ml-2 text-gray-500 font-normal">· {warehouse.name}</span>}
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />
      <div className="p-4 space-y-4 max-w-3xl">
        <StoreSearch warehouseId={warehouse_id} store={store} onSelect={setStore} />
        {store ? renderChecklist() : <p className="text-sm text-gray-500">{t(`${T}.select_prompt`)}</p>}
      </div>
    </PageView>
  )
}

const CandidateRow = ({ candidate, storeId, checked, disabled, onChange }) => {
  const { t } = useTranslation()
  const { id, name, package_type, length_cm, width_cm, height_cm, active, store_id } = candidate
  const hasDimensions = [length_cm, width_cm, height_cm].every(value => value != null)
  const parts = [name, t(`shipping_packages.package_types.${package_type}`, { defaultValue: package_type })]
  if (hasDimensions) parts.push(`${length_cm}×${width_cm}×${height_cm} cm`)

  return (
    <li data-testid={`candidate-${id}`} className={`flex items-center gap-2 px-3 ${active ? '' : 'bg-gray-50 text-gray-400'}`}>
      <Checkbox label={parts.join(' · ')} checked={checked} disabled={disabled} onChange={onChange} />
      {store_id === storeId && <span className="text-xs rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5">{t(`${T}.own_tag`)}</span>}
      {!active && <span className="text-xs rounded-full bg-gray-200 text-gray-600 px-2 py-0.5">{t(`${T}.inactive_tag`)}</span>}
    </li>
  )
}

// Async brand picker: the same debounced `fetchStores` search the box form uses, limited to this warehouse.
const StoreSearch = ({ warehouseId, store, onSelect }) => {
  const { t } = useTranslation()
  const latestSearch = useRef(0)
  const [text, setText] = useState('')
  const [options, setOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchedFor, setSearchedFor] = useState(null)
  const [searchFailed, setSearchFailed] = useState(false)

  const searchStores = useMemo(
    () =>
      debounce(async value => {
        const name = value.trim()
        const seq = ++latestSearch.current
        if (name.length < MIN_QUERY_LENGTH) {
          setOptions([])
          setSearching(false)
          setSearchedFor(null)
          return
        }
        setSearching(true)
        try {
          const res = await fetchStores({ warehouse_id: warehouseId, name, per_page: 20 })
          if (seq !== latestSearch.current) return
          setOptions((res.stores || []).map(s => ({ id: s.id, name: s.name })))
          setSearchFailed(false)
        } catch {
          if (seq !== latestSearch.current) return
          setOptions([])
          setSearchFailed(true)
        }
        setSearching(false)
        setSearchedFor(name)
      }, 300),
    [warehouseId],
  )
  useEffect(
    () => () => {
      searchStores.cancel()
      latestSearch.current += 1
    },
    [searchStores],
  )

  const pick = option => {
    onSelect(option)
    setText('')
    setOptions([])
    setSearchedFor(null)
  }

  if (store) {
    return (
      <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
        <span className="text-sm font-medium text-gray-900">{store.name}</span>
        <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => onSelect(null)}>
          {t(`${T}.change_store`)}
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="store-allowed-boxes-search">
        {t(`${T}.store_label`)}
      </label>
      <input
        id="store-allowed-boxes-search"
        type="text"
        className={inputClasses}
        placeholder={t(`${T}.store_placeholder`)}
        value={text}
        onChange={e => {
          setText(e.target.value)
          searchStores(e.target.value)
        }}
        autoComplete="off"
      />
      {searching ? (
        <p className="mt-1 text-xs text-gray-500">{t(`${T}.searching`)}</p>
      ) : options.length > 0 ? (
        <ul className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-56 overflow-auto bg-white">
          {options.map(option => (
            <li key={option.id}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50" onClick={() => pick(option)}>
                {option.name}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        searchedFor !== null &&
        searchedFor === text.trim() && (
          <p className={`mt-1 text-xs ${searchFailed ? 'text-red-600' : 'text-gray-500'}`}>{t(searchFailed ? `${T}.search_error` : `${T}.no_results`)}</p>
        )
      )}
    </div>
  )
}
